// Bill Pay & Recharge home (spec §6.1) — Bharat Connect badge, bottom tabs
// (Instant Pay · AutoPay · Recent · Billers · Help), saved-biller chips and the
// 4-column grid of all 25 categories on Instant Pay.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBolt,
  faBuilding,
  faBuildingColumns,
  faChartLine,
  faCircle,
  faCreditCard,
  faDroplet,
  faFireFlameSimple,
  faFileInvoice,
  faGasPump,
  faGraduationCap,
  faHandHoldingHeart,
  faHouse,
  faPersonWalking,
  faPhone,
  faRadio,
  faReceipt,
  faRoad,
  faShieldHalved,
  faSatelliteDish,
  faTrainSubway,
  faTv,
  faUsers,
  faWifi,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { CATEGORIES, billerById } from '@/mock/billPayCatalogue';
import { savedBillers, mandates } from '@/api/billPay';
import { runBankClock } from '@/mock/phase4';
import type { SavedBiller } from '@/types/phase4.types';
import '../phase4.css';

const TABS = ['Instant Pay', 'AutoPay', 'Recent', 'Billers', 'Help'] as const;

const CATEGORY_ICONS: Record<string, IconDefinition> = {
  metro: faTrainSubway,
  electricity: faBolt,
  credit_card: faCreditCard,
  mutual_fund: faChartLine,
  fastag: faRoad,
  dth: faSatelliteDish,
  gas: faFireFlameSimple,
  insurance: faShieldHalved,
  landline: faPhone,
  water: faDroplet,
  broadband: faWifi,
  loans: faBuildingColumns,
  lpg: faGasPump,
  taxes: faReceipt,
  donation: faHandHoldingHeart,
  education: faGraduationCap,
  subscription: faRadio,
  nps: faPersonWalking,
  cable_tv: faTv,
  clubs: faUsers,
  housing: faBuilding,
  ncmc: faCircle,
  rental: faHouse,
  others: faFileInvoice,
};

const BillPayHome = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Instant Pay');
  const [billers, setBillers] = useState<SavedBiller[] | null>(null);
  const [autopayCount, setAutopayCount] = useState(0);

  useEffect(() => {
    runBankClock();
    savedBillers().then(setBillers).catch(() => setBillers([]));
    mandates().then((m) => setAutopayCount(m.length)).catch(() => setAutopayCount(0));
  }, []);

  return (
    <div className="nova-p4-page">
      <div className="nova-p4-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/dashboard')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Bill Pay &amp; Recharge</h2>
        <span className="nova-p4-bc-badge">Bharat Connect</span>
      </div>

      <div className="nova-p4-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={`nova-p4-tab ${tab === t ? 'active' : ''}`}
            onClick={() => {
              if (t === 'Instant Pay') setTab(t);
              else navigate(`/pay/bills/${t.toLowerCase().replace(' ', '-')}`);
            }}
          >
            {t === 'AutoPay' && autopayCount > 0 ? `${t} (${autopayCount})` : t}
          </button>
        ))}
      </div>

      {tab === 'Instant Pay' && (
        <>
          {billers && billers.length > 0 && (
            <>
              <h3 className="nova-p4-row-title" style={{ marginBottom: 10 }}>Saved billers</h3>
              <div className="nova-p4-chip-row">
                {billers.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    className="nova-p4-chip"
                    onClick={() => navigate(`/pay/bills/category/${categoryOf(b.billerId)}`, { state: { savedBillerId: b.id } })}
                  >
                    <FontAwesomeIcon icon={faBolt} /> {b.nickname || b.billerId}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="nova-p4-grid">
            {CATEGORIES.filter((c) => c.id !== 'mobile').map((c) => (
              <button
                key={c.id}
                type="button"
                className="nova-p4-tile"
                onClick={() => navigate(`/pay/bills/category/${c.id}`)}
              >
                <span className="nova-p4-tile-icon">
                  <FontAwesomeIcon icon={CATEGORY_ICONS[c.id] ?? faBolt} />
                </span>
                <span className="nova-p4-tile-label">{c.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// small helper to map a saved biller back to its category for navigation
const categoryOf = (billerId: string): string => billerById(billerId)?.categoryId ?? 'others';

export default BillPayHome;
